import React, { useState, useMemo } from 'react';
import {
  AlertCircle,
  Save,
  X,
  Info,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  User,
  Briefcase,
  Building2,
  DollarSign,
  Award,
  FileText,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type {
  ExtractionResult,
  ExtractedCompany,
  ExtractedPerson,
  ExtractedPastPerformance,
  ExtractedPricing,
  ExtractedCapability
} from '../../lib/document-analysis';

interface EnhancedDocumentReviewProps {
  extractionResult: ExtractionResult;
  onComplete: () => void;
  onCancel: () => void;
}

type SelectionState = {
  company: boolean;
  personnel: Set<number>;
  pastPerformance: Set<number>;
  capabilities: Set<number>;
  pricing: boolean;
};

const getConfidenceColor = (score: number): string => {
  if (score >= 0.8) return 'text-success bg-success-bg';
  if (score >= 0.6) return 'text-fit-moderate bg-fit-moderate-bg';
  return 'text-urgent bg-urgent-bg';
};

const ConfidenceBadge = ({ score }: { score: number }) => (
  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${getConfidenceColor(score)}`}>
    {Math.round(score * 100)}%
  </span>
);

export default function EnhancedDocumentReview({
  extractionResult,
  onComplete,
  onCancel
}: EnhancedDocumentReviewProps) {
  const { profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['company', 'personnel', 'pastPerformance', 'capabilities'])
  );

  // Track which items are selected for import
  const [selections, setSelections] = useState<SelectionState>(() => ({
    company: !!extractionResult.company,
    personnel: new Set(extractionResult.personnel.map((_, i) => i)),
    pastPerformance: new Set(extractionResult.pastPerformance.map((_, i) => i)),
    capabilities: new Set(extractionResult.capabilities.map((_, i) => i)),
    pricing: !!extractionResult.pricing && extractionResult.pricing.labor.length > 0
  }));

  // Editable data state
  const [editedCompany, setEditedCompany] = useState<ExtractedCompany | null>(
    extractionResult.company
  );
  const [editedPersonnel, setEditedPersonnel] = useState<ExtractedPerson[]>(
    extractionResult.personnel
  );
  const [editedPastPerformance, setEditedPastPerformance] = useState<ExtractedPastPerformance[]>(
    extractionResult.pastPerformance
  );
  const [editedCapabilities, setEditedCapabilities] = useState<ExtractedCapability[]>(
    extractionResult.capabilities
  );
  const [editedPricing, setEditedPricing] = useState<ExtractedPricing | null>(
    extractionResult.pricing
  );

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const toggleCompanySelection = () => {
    setSelections(prev => ({ ...prev, company: !prev.company }));
  };

  const togglePersonnelSelection = (index: number) => {
    setSelections(prev => {
      const newSet = new Set(prev.personnel);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return { ...prev, personnel: newSet };
    });
  };

  const togglePastPerformanceSelection = (index: number) => {
    setSelections(prev => {
      const newSet = new Set(prev.pastPerformance);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return { ...prev, pastPerformance: newSet };
    });
  };

  const toggleCapabilitiesSelection = (index: number) => {
    setSelections(prev => {
      const newSet = new Set(prev.capabilities);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return { ...prev, capabilities: newSet };
    });
  };

  const togglePricingSelection = () => {
    setSelections(prev => ({ ...prev, pricing: !prev.pricing }));
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    return {
      companyFields: editedCompany ? Object.values(editedCompany).filter(v => v !== null && v !== undefined && v !== '').length : 0,
      personnelCount: editedPersonnel.length,
      pastPerformanceCount: editedPastPerformance.length,
      capabilitiesCount: editedCapabilities.length,
      pricingRoles: editedPricing?.labor?.length || 0,
      selectedPersonnel: selections.personnel.size,
      selectedPastPerformance: selections.pastPerformance.size,
      selectedCapabilities: selections.capabilities.size
    };
  }, [editedCompany, editedPersonnel, editedPastPerformance, editedCapabilities, editedPricing, selections]);

  const handleApplyData = async () => {
    if (!profile?.id) {
      toast.error('No profile found');
      return;
    }

    setSaving(true);
    try {
      // 1. Update company/profile information
      if (selections.company && editedCompany) {
        const profileUpdates: Record<string, any> = {};

        if (editedCompany.companyName) profileUpdates.company_name = editedCompany.companyName;
        if (editedCompany.legalStructure) profileUpdates.legal_structure = editedCompany.legalStructure;
        if (editedCompany.yearEstablished) profileUpdates.year_established = editedCompany.yearEstablished;
        if (editedCompany.dunsNumber) profileUpdates.duns_number = editedCompany.dunsNumber;
        if (editedCompany.ueiNumber) profileUpdates.uei_number = editedCompany.ueiNumber;
        if (editedCompany.cageCode) profileUpdates.cage_code = editedCompany.cageCode;
        if (editedCompany.phone) profileUpdates.phone = editedCompany.phone;
        if (editedCompany.email) profileUpdates.email = editedCompany.email;
        if (editedCompany.website) profileUpdates.website_url = editedCompany.website;
        if (editedCompany.valueProposition) profileUpdates.value_proposition = editedCompany.valueProposition;
        if (editedCompany.securityClearance) profileUpdates.security_clearance_level = editedCompany.securityClearance;
        if (editedCompany.totalEmployees) profileUpdates.total_employees = editedCompany.totalEmployees;
        if (editedCompany.annualRevenue) profileUpdates.annual_revenue = editedCompany.annualRevenue;

        // Arrays - merge with existing
        if (editedCompany.coreCompetencies?.length > 0) {
          const existing = profile.core_competencies || [];
          profileUpdates.core_competencies = [...new Set([...existing, ...editedCompany.coreCompetencies])];
        }
        if (editedCompany.keyTechnologies?.length > 0) {
          const existing = profile.key_technologies || [];
          profileUpdates.key_technologies = [...new Set([...existing, ...editedCompany.keyTechnologies])];
        }
        if (editedCompany.industriesServed?.length > 0) {
          const existing = profile.industries_served || [];
          profileUpdates.industries_served = [...new Set([...existing, ...editedCompany.industriesServed])];
        }
        if (editedCompany.certifications?.length > 0) {
          const existing = profile.certifications || [];
          profileUpdates.certifications = [...new Set([...existing, ...editedCompany.certifications])];
        }
        if (editedCompany.naicsCodes?.length > 0) {
          const existing = profile.naics_codes || [];
          profileUpdates.naics_codes = [...new Set([...existing, ...editedCompany.naicsCodes])];
        }
        if (editedCompany.qualifiedSetAsides?.length > 0) {
          const existing = profile.qualified_set_asides || [];
          profileUpdates.qualified_set_asides = [...new Set([...existing, ...editedCompany.qualifiedSetAsides])];
        }

        // Address
        if (editedCompany.address && (editedCompany.address.street || editedCompany.address.city)) {
          profileUpdates.business_address = {
            street: editedCompany.address.street,
            city: editedCompany.address.city,
            state: editedCompany.address.state,
            zip: editedCompany.address.zip,
            country: 'USA'
          };
        }

        if (Object.keys(profileUpdates).length > 0) {
          const { error } = await supabase
            .from('profiles')
            .update(profileUpdates)
            .eq('id', profile.id);

          if (error) throw error;
          console.log('Updated profile with', Object.keys(profileUpdates).length, 'fields');
        }
      }

      // 2. Upsert key personnel (merge with existing if name/email matches)
      if (selections.personnel.size > 0) {
        // First, fetch existing personnel to check for matches
        const { data: existingPersonnel } = await supabase
          .from('key_personnel')
          .select('id, name, email, certifications, skills_expertise, key_achievements')
          .eq('profile_id', profile.id);

        const findExisting = (p: ExtractedPerson) => {
          if (!existingPersonnel) return null;
          // Match by email first
          if (p.email) {
            const byEmail = existingPersonnel.find(e =>
              e.email?.toLowerCase() === p.email?.toLowerCase()
            );
            if (byEmail) return byEmail;
          }
          // Match by name
          return existingPersonnel.find(e =>
            e.name?.toLowerCase().trim() === p.name.toLowerCase().trim()
          );
        };

        let insertedCount = 0;
        let updatedCount = 0;

        for (const index of selections.personnel) {
          const p = editedPersonnel[index];
          const existing = findExisting(p);

          try {
            if (existing) {
              // Update existing - merge arrays
              const updateData: Record<string, any> = {
                updated_at: new Date().toISOString()
              };

              if (p.title) updateData.title = p.title;
              if (p.yearsExperience) updateData.years_experience = p.yearsExperience;
              if (p.securityClearance && p.securityClearance !== 'none') {
                updateData.security_clearance = p.securityClearance;
              }
              if (p.educationLevel) updateData.education_level = p.educationLevel;
              if (p.phone && !existing.phone) updateData.phone = p.phone;
              if (p.email && !existing.email) updateData.email = p.email;

              // Merge arrays
              if (p.certifications?.length > 0) {
                const existingCerts = existing.certifications || [];
                updateData.certifications = [...new Set([...existingCerts, ...p.certifications])];
              }
              if (p.skillsExpertise?.length > 0) {
                const existingSkills = existing.skills_expertise || [];
                updateData.skills_expertise = [...new Set([...existingSkills, ...p.skillsExpertise])];
              }
              if (p.keyAchievements?.length > 0) {
                const existingAch = existing.key_achievements || [];
                updateData.key_achievements = [...new Set([...existingAch, ...p.keyAchievements])];
              }

              if (Object.keys(updateData).length > 1) {
                const { error } = await supabase
                  .from('key_personnel')
                  .update(updateData)
                  .eq('id', existing.id);

                if (error) {
                  console.error('Failed to update personnel:', p.name, error);
                } else {
                  updatedCount++;
                }
              }
            } else {
              // Insert new
              const { error } = await supabase
                .from('key_personnel')
                .insert({
                  profile_id: profile.id,
                  name: p.name,
                  title: p.title,
                  email: p.email || null,
                  phone: p.phone || null,
                  years_experience: p.yearsExperience || null,
                  security_clearance: p.securityClearance || null,
                  education_level: p.educationLevel || null,
                  certifications: p.certifications?.length > 0 ? p.certifications : null,
                  skills_expertise: p.skillsExpertise?.length > 0 ? p.skillsExpertise : null,
                  key_achievements: p.keyAchievements?.length > 0 ? p.keyAchievements : null
                });

              if (error) {
                console.error('Failed to insert personnel:', p.name, error);
              } else {
                insertedCount++;
              }
            }
          } catch (error) {
            console.error('Error processing personnel:', p.name, error);
          }
        }
        console.log('Personnel: inserted', insertedCount, ', updated', updatedCount);
      }

      // 3. Insert past performance (one at a time to avoid timeout)
      if (selections.pastPerformance.size > 0) {
        const ppToInsert = editedPastPerformance
          .filter((_, i) => selections.pastPerformance.has(i))
          .map(pp => ({
            profile_id: profile.id,
            project_name: pp.projectName,
            agency: pp.agency || null,
            sector_type: pp.sectorType || null,
            contract_type: pp.contractType || null,
            contract_value: pp.contractValue || null,
            start_date: pp.startDate || null,
            end_date: pp.endDate || null,
            deliverables: pp.deliverables || null,
            technologies_used: pp.technologiesUsed?.length > 0 ? pp.technologiesUsed : null,
            key_accomplishments: pp.keyAccomplishments?.length > 0 ? pp.keyAccomplishments : null,
            contract_number: pp.contractNumber || null,
            naics_code: pp.naicsCode || null,
            client_reference: pp.clientReference || null
          }));

        let insertedCount = 0;
        for (const pp of ppToInsert) {
          const { error } = await supabase
            .from('past_performance')
            .insert(pp);

          if (error) {
            console.error('Failed to insert past performance:', pp.project_name, error);
          } else {
            insertedCount++;
          }
        }
        console.log('Inserted', insertedCount, 'of', ppToInsert.length, 'past performance records');
      }

      // 4. Insert capabilities (one at a time, if table exists)
      if (selections.capabilities.size > 0) {
        const capsToInsert = editedCapabilities
          .filter((_, i) => selections.capabilities.has(i))
          .map(c => ({
            profile_id: profile.id,
            capability_name: c.name,
            description: c.description || null,
            proficiency_level: c.proficiencyLevel || null,
            years_experience: c.yearsExperience || null,
            tools_used: c.toolsUsed?.length > 0 ? c.toolsUsed : null,
            differentiators: c.differentiators?.length > 0 ? c.differentiators : null
          }));

        let insertedCount = 0;
        for (const cap of capsToInsert) {
          try {
            const { error } = await supabase
              .from('capabilities')
              .insert(cap);

            if (error && !error.message.includes('does not exist')) {
              console.warn('Failed to insert capability:', cap.capability_name, error);
            } else if (!error) {
              insertedCount++;
            }
          } catch (e) {
            console.warn('Capabilities table may not exist:', e);
            break; // Stop trying if table doesn't exist
          }
        }
        if (insertedCount > 0) {
          console.log('Inserted', insertedCount, 'of', capsToInsert.length, 'capability records');
        }
      }

      // 5. Insert pricing data (one at a time to avoid timeout)
      if (selections.pricing && editedPricing && editedPricing.labor.length > 0) {
        // First create the parent pricing record
        try {
          const { data: pricingRecord, error: pricingError } = await supabase
            .from('enhanced_pricing_data')
            .insert({
              profile_id: profile.id,
              sector: 'General',
              notes: 'Imported from document extraction'
            })
            .select()
            .single();

          if (pricingError) {
            console.error('Failed to create pricing record:', pricingError);
          } else if (pricingRecord) {
            // Insert labor components one at a time
            let insertedCount = 0;
            for (const l of editedPricing.labor) {
              const { error: componentError } = await supabase
                .from('pricing_components')
                .insert({
                  pricing_data_id: pricingRecord.id,
                  category: 'labor',
                  name: l.roleTitle,
                  description: l.laborCategory || '',
                  unit_cost: l.hourlyRate,
                  quantity: 1,
                  unit: 'hour'
                });

              if (componentError) {
                console.error('Failed to insert pricing component:', l.roleTitle, componentError);
              } else {
                insertedCount++;
              }
            }
            console.log('Inserted', insertedCount, 'of', editedPricing.labor.length, 'pricing components');
          }
        } catch (e) {
          console.warn('Pricing tables may not exist:', e);
        }
      }

      toast.success('Profile data imported successfully!');

      // Refresh the profile to show updated data
      if (refreshProfile) {
        await refreshProfile();
      }

      onComplete();

    } catch (error: any) {
      console.error('Error applying extracted data:', error);
      toast.error(`Failed to import data: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-bw-surface rounded-lg shadow-lg max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-bw-surface-subtle">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Sparkles className="h-6 w-6 text-bw-primary mr-2" />
            <h2 className="text-xl font-semibold text-bw-ink">AI Extraction Results</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-bw-muted hover:text-bw-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-sm text-bw-muted">
          {extractionResult.documentSummary}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="px-6 py-3 bg-bw-surface-subtle border-b">
        <div className="flex items-center justify-between text-sm">
          <div className="flex space-x-6">
            <span className="flex items-center text-bw-muted">
              <Building2 className="h-4 w-4 mr-1" />
              {summaryStats.companyFields} company fields
            </span>
            <span className="flex items-center text-bw-muted">
              <User className="h-4 w-4 mr-1" />
              {summaryStats.selectedPersonnel}/{summaryStats.personnelCount} personnel
            </span>
            <span className="flex items-center text-bw-muted">
              <Briefcase className="h-4 w-4 mr-1" />
              {summaryStats.selectedPastPerformance}/{summaryStats.pastPerformanceCount} projects
            </span>
            <span className="flex items-center text-bw-muted">
              <Award className="h-4 w-4 mr-1" />
              {summaryStats.selectedCapabilities}/{summaryStats.capabilitiesCount} capabilities
            </span>
            {summaryStats.pricingRoles > 0 && (
              <span className="flex items-center text-bw-muted">
                <DollarSign className="h-4 w-4 mr-1" />
                {summaryStats.pricingRoles} labor rates
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Extraction Notes */}
      {extractionResult.extractionNotes.length > 0 && (
        <div className="px-6 py-3 bg-fit-moderate-bg border-b">
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-fit-moderate mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-fit-moderate">
              {extractionResult.extractionNotes.map((note, i) => (
                <p key={i}>{note}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

        {/* Company Information Section */}
        {editedCompany && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('company')}
              className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle"
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selections.company}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleCompanySelection();
                  }}
                  className="h-4 w-4 text-bw-primary border-bw-border rounded mr-3"
                />
                <Building2 className="h-5 w-5 text-bw-primary mr-2" />
                <span className="font-medium">Company Information</span>
                <ConfidenceBadge score={editedCompany.confidence} />
              </div>
              {expandedSections.has('company') ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.has('company') && (
              <div className="p-4 pt-0 border-t bg-bw-surface-subtle">
                <div className="grid grid-cols-2 gap-4">
                  {editedCompany.companyName && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">Company Name</label>
                      <input
                        type="text"
                        value={editedCompany.companyName}
                        onChange={(e) => setEditedCompany({ ...editedCompany, companyName: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.dunsNumber && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">DUNS Number</label>
                      <input
                        type="text"
                        value={editedCompany.dunsNumber}
                        onChange={(e) => setEditedCompany({ ...editedCompany, dunsNumber: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.ueiNumber && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">UEI Number</label>
                      <input
                        type="text"
                        value={editedCompany.ueiNumber}
                        onChange={(e) => setEditedCompany({ ...editedCompany, ueiNumber: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.cageCode && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">CAGE Code</label>
                      <input
                        type="text"
                        value={editedCompany.cageCode}
                        onChange={(e) => setEditedCompany({ ...editedCompany, cageCode: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.phone && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">Phone</label>
                      <input
                        type="text"
                        value={editedCompany.phone}
                        onChange={(e) => setEditedCompany({ ...editedCompany, phone: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.email && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">Email</label>
                      <input
                        type="email"
                        value={editedCompany.email}
                        onChange={(e) => setEditedCompany({ ...editedCompany, email: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.website && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">Website</label>
                      <input
                        type="url"
                        value={editedCompany.website}
                        onChange={(e) => setEditedCompany({ ...editedCompany, website: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                  {editedCompany.securityClearance && (
                    <div>
                      <label className="block text-xs font-medium text-bw-muted">Security Clearance</label>
                      <input
                        type="text"
                        value={editedCompany.securityClearance}
                        onChange={(e) => setEditedCompany({ ...editedCompany, securityClearance: e.target.value })}
                        className="mt-1 block w-full text-sm rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                      />
                    </div>
                  )}
                </div>

                {/* Arrays */}
                {editedCompany.naicsCodes?.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-bw-muted mb-1">NAICS Codes</label>
                    <div className="flex flex-wrap gap-2">
                      {editedCompany.naicsCodes.map((code, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary">
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {editedCompany.coreCompetencies?.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-bw-muted mb-1">Core Competencies</label>
                    <div className="flex flex-wrap gap-2">
                      {editedCompany.coreCompetencies.map((comp, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success-bg text-success">
                          {comp}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {editedCompany.certifications?.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-bw-muted mb-1">Certifications</label>
                    <div className="flex flex-wrap gap-2">
                      {editedCompany.certifications.map((cert, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {editedCompany.qualifiedSetAsides?.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-bw-muted mb-1">Set-Aside Qualifications</label>
                    <div className="flex flex-wrap gap-2">
                      {editedCompany.qualifiedSetAsides.map((sa, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-fit-moderate">
                          {sa}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Personnel Section */}
        {editedPersonnel.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('personnel')}
              className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle"
            >
              <div className="flex items-center">
                <User className="h-5 w-5 text-success mr-2" />
                <span className="font-medium">Key Personnel ({editedPersonnel.length})</span>
              </div>
              {expandedSections.has('personnel') ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.has('personnel') && (
              <div className="border-t divide-y">
                {editedPersonnel.map((person, index) => (
                  <div key={index} className="p-4 bg-bw-surface-subtle hover:bg-bw-surface-subtle">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selections.personnel.has(index)}
                        onChange={() => togglePersonnelSelection(index)}
                        className="h-4 w-4 text-bw-primary border-bw-border rounded mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{person.name}</span>
                            <span className="text-bw-muted ml-2">{person.title}</span>
                          </div>
                          <ConfidenceBadge score={person.confidence} />
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 text-sm text-bw-muted">
                          {person.yearsExperience && (
                            <span>{person.yearsExperience} years exp</span>
                          )}
                          {person.securityClearance && (
                            <span className="text-urgent">{person.securityClearance}</span>
                          )}
                          {person.educationLevel && (
                            <span>{person.educationLevel}</span>
                          )}
                        </div>
                        {person.certifications?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {person.certifications.map((cert, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-gray-200 rounded">
                                {cert}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Past Performance Section */}
        {editedPastPerformance.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('pastPerformance')}
              className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle"
            >
              <div className="flex items-center">
                <Briefcase className="h-5 w-5 text-bw-primary mr-2" />
                <span className="font-medium">Past Performance ({editedPastPerformance.length})</span>
              </div>
              {expandedSections.has('pastPerformance') ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.has('pastPerformance') && (
              <div className="border-t divide-y">
                {editedPastPerformance.map((pp, index) => (
                  <div key={index} className="p-4 bg-bw-surface-subtle hover:bg-bw-surface-subtle">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selections.pastPerformance.has(index)}
                        onChange={() => togglePastPerformanceSelection(index)}
                        className="h-4 w-4 text-bw-primary border-bw-border rounded mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{pp.projectName}</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                              pp.sectorType === 'Government' ? 'bg-bw-primary-tint text-bw-primary' : 'bg-bw-surface-subtle text-bw-ink'
                            }`}>
                              {pp.sectorType}
                            </span>
                          </div>
                          <ConfidenceBadge score={pp.confidence} />
                        </div>
                        <div className="mt-1 text-sm text-bw-muted">
                          <span>{pp.agency}</span>
                          {pp.contractValue && (
                            <span className="ml-2">
                              ${pp.contractValue.toLocaleString()}
                            </span>
                          )}
                          {pp.contractType && (
                            <span className="ml-2 text-bw-muted">({pp.contractType})</span>
                          )}
                        </div>
                        {pp.deliverables && (
                          <p className="mt-2 text-sm text-bw-muted line-clamp-2">{pp.deliverables}</p>
                        )}
                        {pp.technologiesUsed?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {pp.technologiesUsed.slice(0, 5).map((tech, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-gray-200 rounded">
                                {tech}
                              </span>
                            ))}
                            {pp.technologiesUsed.length > 5 && (
                              <span className="text-xs text-bw-muted">+{pp.technologiesUsed.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Capabilities Section */}
        {editedCapabilities.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('capabilities')}
              className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle"
            >
              <div className="flex items-center">
                <Award className="h-5 w-5 text-fit-moderate mr-2" />
                <span className="font-medium">Capabilities ({editedCapabilities.length})</span>
              </div>
              {expandedSections.has('capabilities') ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.has('capabilities') && (
              <div className="border-t divide-y">
                {editedCapabilities.map((cap, index) => (
                  <div key={index} className="p-4 bg-bw-surface-subtle hover:bg-bw-surface-subtle">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selections.capabilities.has(index)}
                        onChange={() => toggleCapabilitiesSelection(index)}
                        className="h-4 w-4 text-bw-primary border-bw-border rounded mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{cap.name}</span>
                          <div className="flex items-center">
                            <span className="text-xs px-2 py-0.5 bg-gray-200 rounded mr-2">
                              {cap.proficiencyLevel}
                            </span>
                            <ConfidenceBadge score={cap.confidence} />
                          </div>
                        </div>
                        {cap.description && (
                          <p className="mt-1 text-sm text-bw-muted line-clamp-2">{cap.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pricing Section */}
        {editedPricing && editedPricing.labor.length > 0 && (
          <div className="border rounded-lg">
            <button
              onClick={() => toggleSection('pricing')}
              className="w-full flex items-center justify-between p-4 hover:bg-bw-surface-subtle"
            >
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selections.pricing}
                  onChange={(e) => {
                    e.stopPropagation();
                    togglePricingSelection();
                  }}
                  className="h-4 w-4 text-bw-primary border-bw-border rounded mr-3"
                />
                <DollarSign className="h-5 w-5 text-success mr-2" />
                <span className="font-medium">Pricing Data ({editedPricing.labor.length} roles)</span>
                <ConfidenceBadge score={editedPricing.confidence} />
              </div>
              {expandedSections.has('pricing') ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.has('pricing') && (
              <div className="border-t p-4 bg-bw-surface-subtle">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-bw-muted">
                      <th className="pb-2">Role</th>
                      <th className="pb-2">Hourly Rate</th>
                      <th className="pb-2">Category</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-bw-border">
                    {editedPricing.labor.map((labor, i) => (
                      <tr key={i}>
                        <td className="py-2">{labor.roleTitle}</td>
                        <td className="py-2">${labor.hourlyRate.toFixed(2)}/hr</td>
                        <td className="py-2 text-bw-muted">{labor.laborCategory || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!editedCompany && editedPersonnel.length === 0 && editedPastPerformance.length === 0 && (
          <div className="text-center py-8 text-bw-muted">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p>No structured data was extracted from this document.</p>
            <p className="text-sm mt-2">The document may not contain recognizable profile information.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t bg-bw-surface-subtle flex justify-between items-center">
        <div className="text-sm text-bw-muted">
          Select the data you want to import to your profile
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyData}
            disabled={saving}
            className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <span className="animate-spin mr-2">&#9696;</span>
                Importing...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Import Selected Data
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
